import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, map, shareReplay, switchMap } from 'rxjs';

import { BaseService } from './base-service';
import { CurrentConfigurationsService } from './current-configuration.service';
import { ListRoles$Params } from '@api/backend-connector/fn/roles/list-roles';
import { CreateRoleRequest, GrantType, RoleDto, RoleDtoPage, UpdateRoleRequest } from '@api/backend-connector/models';
import { RolesApi } from '@api/backend-connector/services';

interface GrantTreeNodeDef {
  key: string;
  children?: GrantTreeNodeDef[];
  classes?: string;
}

export const TranslateGrantsMap: Record<GrantType | string, string> = {
  ['all']: 'i18n.Grants.Label.all',
  ['project']: 'i18n.Grants.Label.project',
  ['projectAttributes']: 'i18n.Grants.Label.projectAttributes',
  ['workPackage']: 'i18n.Grants.Label.workPackage',
  ['template']: 'i18n.Grants.Label.template',
  ['collaborators']: 'i18n.Grants.Label.collaborators',
  ['taskAttributes']: 'i18n.Grants.Label.taskAttributes',
  ['assignedTaskAttributes']: 'i18n.Grants.Label.assignedTaskAttributes',
  ['budgets']: 'i18n.Grants.Label.budgets',
  ['inventory']: 'i18n.Grants.Label.inventory',
  ['globalAttributes']: 'i18n.Grants.Label.globalAttributes',
  [GrantType.ViewUsers]: 'i18n.Grants.viewUsers',
  [GrantType.ManageUsers]: 'i18n.Grants.manageUsers',
  [GrantType.ViewRoles]: 'i18n.Grants.viewRoles',
  [GrantType.ManageRoles]: 'i18n.Grants.manageRoles',
  [GrantType.ViewGroups]: 'i18n.Grants.viewGroups',
  [GrantType.ManageGroups]: 'i18n.Grants.manageGroups',
  [GrantType.ViewTeamPlanners]: 'i18n.Grants.viewTeamPlanners',
  [GrantType.ManageTeamPlanners]: 'i18n.Grants.manageTeamPlanners',
  [GrantType.ViewLaborActivities]: 'i18n.Grants.viewLaborActivities',
  [GrantType.ManageLaborActivities]: 'i18n.Grants.manageLaborActivities',
  [GrantType.ViewCostTypes]: 'i18n.Grants.viewCostTypes',
  [GrantType.ManageCostTypes]: 'i18n.Grants.manageCostTypes',
  [GrantType.ViewWorkWorkPackageStatuses]: 'i18n.Grants.viewWorkWorkPackageStatuses',
  [GrantType.ManageWorkPackageStatuses]: 'i18n.Grants.manageWorkPackageStatuses',
  [GrantType.ManageWorkPackageTypes]: 'i18n.Grants.manageWorkPackageTypes',
  [GrantType.ViewWorkPackageTypes]: 'i18n.Grants.viewWorkPackageTypes',
  [GrantType.ManageWorkPackagePriorities]: 'i18n.Grants.manageWorkPackagePriorities',
  [GrantType.ViewWorkPackagePriorities]: 'i18n.Grants.viewWorkPackagePriorities',
  [GrantType.ViewProject]: 'i18n.Grants.viewProject',
  [GrantType.CreateProject]: 'i18n.Grants.createProject',
  [GrantType.EditProject]: 'i18n.Grants.editProject',
  [GrantType.EditProjectArchived]: 'i18n.Grants.editProjectArchived',
  [GrantType.EditProjectInfo]: 'i18n.Grants.editProjectInfo',
  [GrantType.EditProjectDates]: 'i18n.Grants.editProjectDates',
  [GrantType.EditProjectPublic]: 'i18n.Grants.editProjectPublic',
  [GrantType.EditProjectStatus]: 'i18n.Grants.editProjectStatus',
  [GrantType.DeleteProject]: 'i18n.Grants.deleteProject',
  [GrantType.ViewWorkPackages]: 'i18n.Grants.viewWorkPackages',
  [GrantType.CreateWorkPackages]: 'i18n.Grants.createWorkPackages',
  [GrantType.EditWorkPackages]: 'i18n.Grants.editWorkPackages',
  [GrantType.EditWorkPackagesInfo]: 'i18n.Grants.editWorkPackagesInfo',
  [GrantType.EditWorkPackagesDates]: 'i18n.Grants.editWorkPackagesDates',
  [GrantType.EditWorkPackagesStatus]: 'i18n.Grants.editWorkPackagesStatus',
  [GrantType.EditWorkPackagesType]: 'i18n.Grants.editWorkPackagesType',
  [GrantType.EditWorkPackagesPriority]: 'i18n.Grants.editWorkPackagesPriority',
  [GrantType.EditWorkPackagesAssignee]: 'i18n.Grants.editWorkPackagesAssignee',
  [GrantType.EditWorkPackagesTargetGroup]: 'i18n.Grants.editWorkPackagesTargetGroup',
  [GrantType.EditWorkPackagesHierarchy]: 'i18n.Grants.editWorkPackagesHierarchy',
  [GrantType.EditWorkPackagesCategories]: 'i18n.Grants.editWorkPackagesCategories',
  [GrantType.EditWorkPackagesDynamicFormsSchema]: 'i18n.Grants.editWorkPackagesDynamicFormsSchema',
  [GrantType.EditWorkPackagesDynamicFormsValue]: 'i18n.Grants.editWorkPackagesDynamicFormsValue',
  [GrantType.EditWorkPackagesInventoryTransactions]: 'i18n.Grants.editWorkPackagesInventoryTransactions',
  [GrantType.DeleteWorkPackages]: 'i18n.Grants.deleteWorkPackages',
  [GrantType.ExportProjectWorkPackages]: 'i18n.Grants.exportProjectWorkPackages',
  [GrantType.ViewBudgets]: 'i18n.Grants.viewBudgets',
  [GrantType.UpdateBudgets]: 'i18n.Grants.updateBudgets',
  [GrantType.CreateBudget]: 'i18n.Grants.createBudget',
  [GrantType.ManageEstimates]: 'i18n.Grants.manageEstimates',
  [GrantType.ManageEffective]: 'i18n.Grants.manageEffective',
  [GrantType.ManageSelfLaborCost]: 'i18n.Grants.manageSelfLaborCost',
  [GrantType.ViewCashAnalytics]: 'i18n.Grants.viewCashAnalytics',
  [GrantType.ViewCostAnalytics]: 'i18n.Grants.viewCostAnalytics',
  [GrantType.ViewProjectCollaborators]: 'i18n.Grants.viewProjectCollaborators',
  [GrantType.ManageProjectUsersMemberships]: 'i18n.Grants.manageProjectUsersMemberships',
  [GrantType.ManageProjectGroupsMemberships]: 'i18n.Grants.manageProjectGroupsMemberships',
  [GrantType.ViewGlobalConfigurations]: 'i18n.Grants.viewGlobalConfigurations',
  [GrantType.ManageGlobalConfigurations]: 'i18n.Grants.manageGlobalConfigurations',
  [GrantType.EditAssignedWorkPackagesInfo]: 'i18n.Grants.editAssignedWorkPackagesInfo',
  [GrantType.EditAssignedWorkPackagesDates]: 'i18n.Grants.editAssignedWorkPackagesDates',
  [GrantType.EditAssignedWorkPackagesStatus]: 'i18n.Grants.editAssignedWorkPackagesStatus',
  [GrantType.EditAssignedWorkPackagesType]: 'i18n.Grants.editAssignedWorkPackagesType',
  [GrantType.EditAssignedWorkPackagesPriority]: 'i18n.Grants.editAssignedWorkPackagesPriority',
  [GrantType.EditAssignedWorkPackagesAssignee]: 'i18n.Grants.editAssignedWorkPackagesAssignee',
  [GrantType.EditAssignedWorkPackagesTargetGroup]: 'i18n.Grants.editAssignedWorkPackagesTargetGroup',
  [GrantType.EditAssignedWorkPackagesHierarchy]: 'i18n.Grants.editAssignedWorkPackagesHierarchy',
  [GrantType.EditAssignedWorkPackagesDynamicFormsSchema]: 'i18n.Grants.editAssignedWorkPackagesDynamicFormsSchema',
  [GrantType.EditAssignedWorkPackagesDynamicFormsValue]: 'i18n.Grants.editAssignedWorkPackagesDynamicFormsValue',
  [GrantType.EditAssignedWorkPackagesInventoryTransactions]:
    'i18n.Grants.editAssignedWorkPackagesInventoryTransactions',
  [GrantType.EditAssignedWorkPackagesCategories]: 'i18n.Grants.editAssignedWorkPackagesCategories',
  [GrantType.ManageCustomFieldsSchema]: 'i18n.Grants.manageCustomFieldsSchema',
  [GrantType.ManageWorkPackageFieldsSchema]: 'i18n.Grants.manageWorkPackageFieldsSchema',
  [GrantType.ManageTemplates]: 'i18n.Grants.manageTemplates',
  [GrantType.ViewTemplates]: 'i18n.Grants.viewTemplates',
  [GrantType.ViewInventoryItems]: 'i18n.Grants.viewInventoryItems',
  [GrantType.ManageInventoryItems]: 'i18n.Grants.manageInventoryItems',
  [GrantType.ViewInventoryTransactions]: 'i18n.Grants.viewInventoryTransactions',
  [GrantType.ManageInventoryTransactions]: 'i18n.Grants.manageInventoryTransactions',
};

export const grantTreeDef = (economicsEnabled: boolean): GrantTreeNodeDef[] => {
  const projects = {
    key: 'project',
    classes: 'highlight category',
    children: [
      {
        key: GrantType.ViewProject,
      },
      {
        key: GrantType.CreateProject,
      },
      {
        key: GrantType.EditProject,
      },
      {
        key: GrantType.DeleteProject,
      },
    ],
  };

  const projectAttributes = {
    key: 'projectAttributes',
    classes: 'highlight category',
    children: [
      {
        key: GrantType.EditProjectInfo,
      },
      {
        key: GrantType.EditProjectDates,
      },
      {
        key: GrantType.EditProjectStatus,
      },
      {
        key: GrantType.EditProjectPublic,
      },
      {
        key: GrantType.EditProjectArchived,
      },
    ],
  };

  const workPackage = {
    key: 'workPackage',
    classes: 'highlight category',
    children: [
      {
        key: GrantType.CreateWorkPackages,
      },
      {
        key: GrantType.ViewWorkPackages,
      },
      {
        key: GrantType.EditWorkPackages,
      },
      {
        key: GrantType.DeleteWorkPackages,
      },
      {
        key: GrantType.ExportProjectWorkPackages,
      },
    ],
  };
  const template = {
    key: 'template',
    classes: 'highlight category',
    children: [
      {
        key: GrantType.ManageTemplates,
      },
      {
        key: GrantType.ViewTemplates,
      },
    ],
  };

  const collaborators = {
    key: 'collaborators',
    classes: 'highlight category',
    children: [
      {
        key: GrantType.ViewProjectCollaborators,
      },
      {
        key: GrantType.ManageProjectGroupsMemberships,
      },
      {
        key: GrantType.ManageProjectUsersMemberships,
      },
    ],
  };

  const taskAttributes = {
    key: 'taskAttributes',
    classes: 'highlight category',
    children: [
      {
        key: GrantType.EditWorkPackagesInfo,
      },
      {
        key: GrantType.EditWorkPackagesDates,
      },
      {
        key: GrantType.EditWorkPackagesStatus,
      },
      {
        key: GrantType.EditWorkPackagesType,
      },
      {
        key: GrantType.EditWorkPackagesPriority,
      },
      {
        key: GrantType.EditWorkPackagesAssignee,
      },
      {
        key: GrantType.EditWorkPackagesTargetGroup,
      },
      {
        key: GrantType.EditWorkPackagesHierarchy,
      },
      {
        key: GrantType.EditWorkPackagesCategories,
      },
      {
        key: GrantType.EditWorkPackagesDynamicFormsSchema,
      },
      {
        key: GrantType.EditWorkPackagesDynamicFormsValue,
      },
      {
        key: GrantType.EditWorkPackagesInventoryTransactions,
      },
    ],
  };

  const assignedTasks = {
    key: 'assignedTaskAttributes',
    classes: 'highlight category',
    children: [
      {
        key: GrantType.EditAssignedWorkPackagesInfo,
      },

      {
        key: GrantType.EditAssignedWorkPackagesDates,
      },

      {
        key: GrantType.EditAssignedWorkPackagesStatus,
      },

      {
        key: GrantType.EditAssignedWorkPackagesType,
      },

      {
        key: GrantType.EditAssignedWorkPackagesPriority,
      },

      {
        key: GrantType.EditAssignedWorkPackagesAssignee,
      },
      {
        key: GrantType.EditAssignedWorkPackagesTargetGroup,
      },

      {
        key: GrantType.EditAssignedWorkPackagesHierarchy,
      },
      {
        key: GrantType.EditAssignedWorkPackagesCategories,
      },
      {
        key: GrantType.EditAssignedWorkPackagesDynamicFormsSchema,
      },
      {
        key: GrantType.EditAssignedWorkPackagesDynamicFormsValue,
      },
      {
        key: GrantType.EditAssignedWorkPackagesInventoryTransactions,
      },
    ],
  };

  const budget = {
    key: 'budgets',
    classes: 'highlight category',
    children: [
      {
        key: GrantType.ViewBudgets,
      },
      {
        key: GrantType.UpdateBudgets,
      },
      {
        key: GrantType.CreateBudget,
      },
      {
        key: GrantType.ManageEstimates,
      },
      {
        key: GrantType.ManageEffective,
      },
      {
        key: GrantType.ManageSelfLaborCost,
      },
      {
        key: GrantType.ViewCashAnalytics,
      },
      {
        key: GrantType.ViewCostAnalytics,
      },
    ],
  };

  const inventory = {
    key: 'inventory',
    classes: 'highlight category',
    children: [
      {
        key: GrantType.ViewInventoryItems,
      },
      {
        key: GrantType.ManageInventoryItems,
      },
      {
        key: GrantType.ViewInventoryTransactions,
      },
      {
        key: GrantType.ManageInventoryTransactions,
      },
    ],
  };

  const globalAttributes = {
    key: 'globalAttributes',
    classes: 'highlight category',
    children: [
      {
        key: GrantType.ViewUsers,
      },
      {
        key: GrantType.ManageUsers,
      },
      {
        key: GrantType.ViewRoles,
      },
      {
        key: GrantType.ManageRoles,
      },
      {
        key: GrantType.ViewGroups,
      },
      {
        key: GrantType.ManageGroups,
      },
      // {
      //   key: GrantType.ViewTeamPlanners,
      // },
      // {
      //   key: GrantType.ManageTeamPlanners,
      // },
      economicsEnabled
        ? {
          key: GrantType.ViewLaborActivities,
        }
        : undefined,
      economicsEnabled
        ? {
          key: GrantType.ManageLaborActivities,
        }
        : undefined,
      economicsEnabled
        ? {
          key: GrantType.ViewCostTypes,
        }
        : undefined,
      economicsEnabled
        ? {
          key: GrantType.ManageCostTypes,
        }
        : undefined,
      {
        key: GrantType.ViewWorkWorkPackageStatuses,
      },
      {
        key: GrantType.ManageWorkPackageStatuses,
      },
      {
        key: GrantType.ViewWorkPackageTypes,
      },
      {
        key: GrantType.ManageWorkPackageTypes,
      },
      {
        key: GrantType.ViewWorkPackagePriorities,
      },
      {
        key: GrantType.ManageWorkPackagePriorities,
      },
      {
        key: GrantType.ViewGlobalConfigurations,
      },
      {
        key: GrantType.ManageGlobalConfigurations,
      },
    ].filter((a) => !!a) as GrantTreeNodeDef[],
  };
  const tree: GrantTreeNodeDef = {
    key: 'all',
    classes: 'highlight root',
    children: [],
  };

  tree.children!.push(
    ...[projects, projectAttributes, workPackage, template, collaborators, taskAttributes, assignedTasks],
  );
  if (economicsEnabled) {
    tree.children!.push(budget);
  }
  tree.children!.push(...[inventory, globalAttributes]);
  return [tree];
};

export interface FlatGrant {
  key: string;
  parent?: string;
}

export const getFlatGrants = (budgetModuleEnabled: boolean): FlatGrant[] => {
  return grantTreeDef(budgetModuleEnabled)
    .at(0)!
    .children!.map((node) => {
    const leaves = node.children?.map((leaf) => ({
      key: leaf.key,
      parent: node.key,
    }));
    return [
      {
        key: node.key,
      },
      ...(leaves ?? []),
    ] as FlatGrant[];
  })
    .flat();
};

const toasts = {
  createSuccessSummary: 'i18n.RoleToast.createSuccessSummary',
  createSuccessDetail: 'i18n.RoleToast.createSuccessDetail',
  createFailSummary: 'i18n.RoleToast.createFailSummary',
  createFailDetail: 'i18n.RoleToast.createFailDetail',

  updateSuccessSummary: 'i18n.Toast.updateSuccessSummary',
  updateSuccessDetail: 'i18n.Toast.updateSuccessDetail',
  updateFailSummary: 'i18n.Toast.updateFailSummary',
  updateFailDetail: 'i18n.Toast.updateFailDetail',

  deleteSuccessSummary: 'i18n.RoleToast.deleteSuccessSummary',
  deleteSuccessDetail: 'i18n.RoleToast.deleteSuccessDetail',
  deleteFailSummary: 'i18n.RoleToast.deleteFailSummary',
  deleteFailDetail: 'i18n.RoleToast.deleteFailDetail',
};

@Injectable({
  providedIn: 'root',
})
export class RolesService extends BaseService {
  private readonly currentConfigurationsService = inject(CurrentConfigurationsService);

  private readonly rolesApi = inject(RolesApi);
  readonly toasts = toasts;

  readonly refresh$ = new BehaviorSubject<void>(undefined);

  // FIXME check page count
  private readonly _allRoles$ = this.refresh$.pipe(
    switchMap(() => this.listRoles({ limit: 100 })),
    map((page) => page.items),
    shareReplay({ refCount: false, bufferSize: 1 }),
  );

  listRoles(params: ListRoles$Params): Observable<RoleDtoPage> {
    return this.rolesApi.listRoles(params);
  }

  getAllRoles() {
    return this._allRoles$;
  }

  getAllDefaultRoles() {
    return this._allRoles$.pipe(map((roles) => roles.filter((role) => role.isDefault)));
  }

  listAddableRoles(
    params: Parameters<typeof RolesService.prototype.rolesApi.listAddableRoles>[0],
  ): Observable<RoleDtoPage> {
    return this.rolesApi.listAddableRoles(params);
  }

  getRole(params: { id: string }): Observable<RoleDto> {
    return this.rolesApi.getRole({
      ...params,
    });
  }

  createRole(request: CreateRoleRequest) {
    return this.rolesApi.createRole({ body: request });
  }

  updateRole(id: string, request: UpdateRoleRequest) {
    return this.rolesApi.updateRole({ id, body: request });
  }

  deleteRole(params: { id: string }) {
    return this.rolesApi.deleteRole(params);
  }

  readonly grantTreeDef$ = this.currentConfigurationsService
    .budgetModuleEnabled()
    .pipe(map((budgetModuleEnabled) => grantTreeDef(budgetModuleEnabled)));
}
